import { unlockGamificationAudio } from '@/components/gamification/gamificationAudio';
import { useAuth } from '@/contexts/AuthContext';
import { getMyPoints, getMultiplierStatus } from '@/services/generatedAPIs';
import { StudentPointsResponse, UserInfoResponseRole, MultiplierStatusResponse } from '@/services/generatedAPIs.schemas';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type GamificationContextValue = {
  kp: number;
  gs: number;
  addKp: (delta: number) => void;
  addGs: (delta: number) => void;
  setKp: (value: number) => void;
  setGs: (value: number) => void;
  refreshPoints: () => Promise<void>;
  multiplierStatus: MultiplierStatusResponse | null;
  refreshMultiplier: () => Promise<void>;
  seasonStartDate?: string;
  currentMonth?: string;
  kpAllTime: number;
  gsAllTime: number;
  gamificationEnabled: boolean;
  leaderboardEnabled: boolean;
};

const GamificationContext = createContext<GamificationContextValue | undefined>(undefined);

export function GamificationProvider({ children }: { children: React.ReactNode }) {
  const { getCurrentUser } = useAuth();
  const [kp, setKp] = useState(0);
  const [gs, setGs] = useState(0);
  const [kpAllTime, setKpAllTime] = useState(0);
  const [gsAllTime, setGsAllTime] = useState(0);
  // 倍率(MPL)提升到全局态:Header 随各页 ContentLayout 反复重挂,若倍率是 Header 局部态,
  // 每次进内页都会重置为 null → MPL 列先消失再出现(闪一下)。放到这里跨页面切换持续。
  const [multiplierStatus, setMultiplierStatus] = useState<MultiplierStatusResponse | null>(null);
  const [seasonStartDate, setSeasonStartDate] = useState<string>();
  const [currentMonth, setCurrentMonth] = useState<string>();
  const [gamificationEnabled, setGamificationEnabled] = useState(true);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const hasFetched = useRef(false);

  const fetchPoints = useCallback(async () => {
    try {
      const rsp = await getMyPoints();
      if (rsp.code === 200 && rsp.data) {
        setKp(rsp.data.kpCurrentMonth ?? 0);
        setGs(rsp.data.gsCurrentMonth ?? 0);
        setKpAllTime(rsp.data.kpAllTime ?? 0);
        setGsAllTime(rsp.data.gsAllTime ?? 0);
        setSeasonStartDate(rsp.data.seasonStartDate);
        setCurrentMonth(rsp.data.currentMonth);
        setGamificationEnabled(rsp.data.gamificationEnabled !== false);
        setLeaderboardEnabled(rsp.data.leaderboardEnabled !== false);
      }
    } catch (error) {
      console.warn('Failed to fetch gamification points:', error);
    }
  }, []);

  // 静默刷新倍率:只在成功拿到新值时 setState,从不清回 null,故进内页时旧值一直在屏,不闪。
  const fetchMultiplier = useCallback(async () => {
    try {
      const rsp = await getMultiplierStatus();
      if (rsp.code === 200 && rsp.data) {
        setMultiplierStatus(rsp.data);
      }
    } catch (error) {
      console.warn('Failed to fetch multiplier status:', error);
    }
  }, []);

  // Fetch points on mount when student is authenticated
  useEffect(() => {
    const user = getCurrentUser();
    if (user?.role === UserInfoResponseRole.STUDENT && !hasFetched.current) {
      hasFetched.current = true;
      fetchPoints();
    } else if (!user || user.role !== UserInfoResponseRole.STUDENT) {
      // Reset hasFetched and clear state when user logs out or is not a student
      hasFetched.current = false;
      setKp(0);
      setGs(0);
      setKpAllTime(0);
      setGsAllTime(0);
      setMultiplierStatus(null);
      setSeasonStartDate(undefined);
      setCurrentMonth(undefined);
    }
  }, [getCurrentUser, fetchPoints]);

  useEffect(() => {
    if (!gamificationEnabled) return;

    let unlocked = false;
    const unlockOnce = () => {
      if (unlocked) return;
      unlocked = true;
      void unlockGamificationAudio();
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
    };

    window.addEventListener('pointerdown', unlockOnce, { passive: true });
    window.addEventListener('keydown', unlockOnce);
    return () => {
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
    };
  }, [gamificationEnabled]);

  const value = useMemo<GamificationContextValue>(() => {
    const addKp = (delta: number) => { if (!gamificationEnabled) return; setKp((current) => current + delta); setKpAllTime((current) => current + delta); };
    const addGs = (delta: number) => { if (!gamificationEnabled) return; setGs((current) => current + delta); setGsAllTime((current) => current + delta); };
    return { kp, gs, addKp, addGs, setKp, setGs, refreshPoints: fetchPoints, multiplierStatus, refreshMultiplier: fetchMultiplier, seasonStartDate, currentMonth, kpAllTime, gsAllTime, gamificationEnabled, leaderboardEnabled };
  }, [kp, gs, fetchPoints, multiplierStatus, fetchMultiplier, seasonStartDate, currentMonth, kpAllTime, gsAllTime, gamificationEnabled, leaderboardEnabled]);

  return <GamificationContext.Provider value={value}>{children}</GamificationContext.Provider>;
}

export function useGamification() {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
}
